import { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro"

import "./acordeonCobros.css"

/*
	Cuentas por cobrar, una fila por ALUMNO.

	Antes era una fila por cobro: un alumno con inscripcion y doce mensualidades
	aparecia trece veces, y como la consulta no traia orden salian salteadas por
	toda la pantalla. Esteban (2026-09-21): "esta totalmente indescifrable, no
	entiendo ni cual es el punto de ese modulo".

	Ahora cada alumno es un renglon con lo unico que se viene a ver -- cuanto
	debe y cuanto de eso ya esta vencido -- y sus cobros se abren debajo.

	Ojo con la paginacion: esto agrupa lo que viene en la pagina. El servidor ya
	ordena por alumno, asi que los cobros de una persona salen juntos; solo el
	alumno que cae justo en el corte puede quedar partido entre dos paginas.
*/

export interface CobroDeAlumno
{
	clave       : string | number
	concepto    : string
	descripcion?: string
	fecha       : string
	subTotal    : number
	pagado      : number
	estatus     : string
	/** Null cuando el cobro todavia no esta facturado. */
	facturaId?  : number | null
	onFactura?  : () => void
	anulado?    : boolean
}

export interface AlumnoConCobros
{
	clave       : string | number
	nombre      : string
	correlativo?: string
	cobros      : CobroDeAlumno[]
}

interface Props
{
	alumnos  : AlumnoConCobros[]
	/** Como escribir una cantidad; la moneda la pone quien llama. */
	dinero   : (valor : number) => string
	vacio?   : string
	idleLocked? : boolean
}

const AcordeonCobros = ({alumnos, dinero, vacio, idleLocked} : Props) =>
{
	const [abiertos, setAbiertos] = useState<{[clave : string] : boolean}>({})

	const resumidos = useMemo
	(
		() => alumnos.map((alumno) =>
		{
			//Lo anulado no se debe, asi que no entra en la cuenta.
			const vivos    = alumno.cobros.filter((cobro) => !cobro.anulado)
			const total    = vivos.reduce((suma, cobro) => suma + cobro.subTotal, 0)
			const pagado   = vivos.reduce((suma, cobro) => suma + cobro.pagado, 0)
			const sinPagar = vivos.filter((cobro) => cobro.subTotal - cobro.pagado > 0.005)

			return {...alumno, total, pagado, debe : total - pagado, pendientes : sinPagar.length}
		}),
		[alumnos]
	)

	if(resumidos.length === 0)
	{
		return <p className="waAcVacio">
			{vacio || "No hay cobros que mostrar."}
		</p>
	}

	return <div className="waAc">{
		resumidos.map((alumno) =>
		{
			const abierto = !!abiertos[alumno.clave]

			return <article className="waAcAlumno" key={alumno.clave}>
				<button
					type="button"
					className="waAcCabecera"
					aria-expanded={abierto}
					onClick={() => setAbiertos({...abiertos, [alumno.clave] : !abierto})}
				>
					<span className="waAcFlecha">
						<FontAwesomeIcon icon={abierto ? solid("chevron-down") : solid("chevron-right")} />
					</span>
					<span className="waAcQuien">
						<span className="waAcNombre">
							{alumno.nombre}
						</span>
						{
							!alumno.correlativo ? null : <span className="waAcCorrelativo">
								{alumno.correlativo}
							</span>
						}
					</span>
					<span className="waAcCuentas">
						<span className="waAcDato">
							<span className="waAcEtiqueta">
								Cobros
							</span>
							<span className="waAcValor">
								{alumno.cobros.length}
							</span>
						</span>
						<span className="waAcDato">
							<span className="waAcEtiqueta">
								Debe
							</span>
							<span className={alumno.debe > 0.005 ? "waAcValor debe" : "waAcValor"}>
								{dinero(alumno.debe)}
							</span>
						</span>
					</span>
				</button>

				{
					!abierto ? null : <div className="waAcCobros">{
						alumno.cobros.map((cobro) => <div
							className={cobro.anulado ? "waAcCobro anulado" : "waAcCobro"}
							key={cobro.clave}
						>
							<span className="waAcConcepto">
								<span className="waAcConceptoNombre">
									{cobro.concepto}
								</span>
								<span className="waAcConceptoPie">
									{[cobro.fecha, cobro.estatus].filter(Boolean).join(" · ")}
								</span>
								{
									!cobro.descripcion ? null : <span className="waAcConceptoPie">
										{cobro.descripcion}
									</span>
								}
							</span>
							<span className="waAcMontos">
								<span className="waAcDato">
									<span className="waAcEtiqueta">
										Monto
									</span>
									<span className="waAcValor">
										{dinero(cobro.subTotal)}
									</span>
								</span>
								<span className="waAcDato">
									<span className="waAcEtiqueta">
										Pagado
									</span>
									<span className="waAcValor">
										{dinero(cobro.pagado)}
									</span>
								</span>
							</span>
							{
								cobro.facturaId == null ? <span className="waAcSinFactura">
									Sin factura
								</span> : <button
									type="button"
									className="waAcFactura"
									disabled={idleLocked}
									onClick={cobro.onFactura}
								>
									Factura {cobro.facturaId}
								</button>
							}
						</div>)
					}</div>
				}
			</article>
		})
	}</div>
}

export default AcordeonCobros
